import React, { useEffect, useState } from 'react';
import { Facebook, Instagram, MessageCircle, ChevronRight, MapPin, Phone, Mail } from 'lucide-react';
import { Link, useLocation, useNavigate, useMatch } from 'react-router-dom';

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingScroll, setPendingScroll] = useState<string | null>(null);
  const isIndexPage = useMatch('/');

  const scrollToSection = (sectionId: string) => {
    if (!isIndexPage) {
      setPendingScroll(sectionId);
      navigate('/', { replace: true });
    } else {
      const section = document.getElementById(sectionId) || 
        document.querySelector(`section:has(#${sectionId})`) ||
        document.querySelector(`.bg-\\[\\#F2F3F4\\]`);
      section?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (isIndexPage && pendingScroll) {
      const scrollTimer = setTimeout(() => {
        const section = document.getElementById(pendingScroll) || 
          document.querySelector(`section:has(#${pendingScroll})`) ||
          document.querySelector(`.bg-\\[\\#F2F3F4\\]`);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
        setPendingScroll(null);
      }, 500);
      return () => clearTimeout(scrollTimer);
    }
  }, [isIndexPage, pendingScroll]);

  return (
    <footer className="bg-[#2A382A] text-white border-t border-white/20">
      <div className="px-4 py-8 md:py-16 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          {/* Top Section */}
          <div className="flex flex-col lg:flex-row lg:justify-between mb-8 lg:mb-12 gap-8">
            {/* Left Column - Contact Info */}
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <img src="/assets/logo-inversed-new.webp" alt="Sierpe Frogs" className="w-12 h-12" />
                <span className="font-barrio text-3xl">SIERPE FROGS</span>
              </div>
              <address className="not-italic text-white/70 space-y-3">
                <a 
                  href="https://maps.app.goo.gl/RCJkAF6xdiVyPVNn8" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label="View our location on Google Maps"
                  className="flex items-center gap-2 hover:text-white underline decoration-white/70 hover:decoration-white"
                >
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  <span>Blvd. Villalobos, Provincia de Puntarenas, Sierpe, 60503, Costa Rica</span>
                </a>
                <a 
                  href="mailto:sierpefrogs@gmail.com"
                  aria-label="Email us at sierpefrogs@gmail.com"
                  className="flex items-center gap-2 hover:text-white underline decoration-white/70 hover:decoration-white"
                >
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <span>sierpefrogs@gmail.com</span>
                </a>
                <a 
                  href="tel:+50689190917" 
                  aria-label="Call us at +506 8919 0917"
                  className="flex items-center gap-2 hover:text-white underline decoration-white/70 hover:decoration-white"
                >
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <span>+506 8919 0917</span>
                </a>
              </address>
              <div className="flex gap-6">
                <a 
                  href="https://facebook.com/sierpe.frogs" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label="Visit our Facebook page"
                  className="w-10 h-10 bg-white rounded-full flex items-center justify-center group transition-all hover:scale-110"
                >
                  <Facebook className="w-5 h-5 text-[#E85C0D] group-hover:text-[#953B0E] transition-colors" />
                </a>
                <a 
                  href="https://instagram.com/sierpe_frogs" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label="Follow us on Instagram"
                  className="w-10 h-10 bg-white rounded-full flex items-center justify-center group transition-all hover:scale-110"
                >
                  <Instagram className="w-5 h-5 text-[#E85C0D] group-hover:text-[#953B0E] transition-colors" />
                </a>
                <a 
                  href="https://wa.me/50689190917" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label="Contact us on WhatsApp"
                  className="w-10 h-10 bg-white rounded-full flex items-center justify-center group transition-all hover:scale-110"
                >
                  <MessageCircle className="w-5 h-5 text-[#E85C0D] group-hover:text-[#953B0E] transition-colors" />
                </a>
              </div>
            </div>

            {/* Center Column */}
            <div className="grid grid-cols-2 gap-8 lg:gap-16">
              <div>
                <ul className="space-y-2">
                  <li>
                    <button 
                      onClick={() => {
                        if (isIndexPage) {
                          scrollToSection('adventures');
                        } else {
                          navigate('/', { replace: true });
                          setPendingScroll('adventures');
                        }
                      }}
                      aria-label="View all tours"
                      className="text-white/70 hover:text-[#E85C0D] flex items-center gap-2"
                    >
                      <ChevronRight className="w-4 h-4 font-bold" />
                      All Tours
                    </button>
                  </li>
                  <li>
                    <Link 
                      to="/tours/sierpe-frog-tour"
                      className="text-white/70 hover:text-[#E85C0D] flex items-center gap-2"
                    >
                      <ChevronRight className="w-4 h-4 font-bold" />
                      Sierpe Frog Tour
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/tours/drake-bay-herping-tour"
                      className="text-white/70 hover:text-[#E85C0D] flex items-center gap-2"
                    >
                      <ChevronRight className="w-4 h-4 font-bold" />
                      Drake Bay Herping Tour
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/tours/uvita-frog-tour"
                      className="text-white/70 hover:text-[#E85C0D] flex items-center gap-2"
                    >
                      <ChevronRight className="w-4 h-4 font-bold" />
                      Uvita Frog Tour
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/tours/wildlife-and-mangrove-river-tour"
                      className="text-white/70 hover:text-[#E85C0D] flex items-center gap-2"
                    >
                      <ChevronRight className="w-5 h-5 font-bold" />
                      Wildlife and Mangrove River Tour
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <ul className="space-y-2">
                  <li>
                    <button 
                      onClick={() => {
                        if (isIndexPage) {
                          scrollToSection('testimonials');
                        } else {
                          navigate('/', { replace: true });
                          setPendingScroll('testimonials');
                        }
                      }}
                      aria-label="View reviews"
                      className="text-white/70 hover:text-[#E85C0D] flex items-center gap-2"
                    >
                      <ChevronRight className="w-5 h-5 font-bold" />
                      Reviews
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => {
                        if (isIndexPage) {
                          scrollToSection('news');
                        } else {
                          navigate('/', { replace: true });
                          setPendingScroll('news');
                        }
                      }}
                      aria-label="View news and updates"
                      className="text-white/70 hover:text-[#E85C0D] flex items-center gap-2"
                    >
                      <ChevronRight className="w-5 h-5 font-bold" />
                      News & Updates
                    </button>
                  </li>
                  <li>
                    <Link 
                      to="/scientific-work"
                      className="text-white/70 hover:text-[#E85C0D] flex items-center gap-2"
                    >
                      <ChevronRight className="w-5 h-5 font-bold" />
                      Scientific Work
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/about-us"
                      className="text-white/70 hover:text-[#E85C0D] flex items-center gap-2"
                    >
                      <ChevronRight className="w-5 h-5 font-bold" />
                      About Us
                    </Link>
                  </li>
                  <li>
                    <button
                      onClick={() => {
                        if (isIndexPage) {
                          scrollToSection('contact');
                        } else {
                          navigate('/', { replace: true });
                          setPendingScroll('contact');
                        }
                      }}
                      aria-label="View contact information"
                      className="text-white/70 hover:text-[#E85C0D] flex items-center gap-2"
                    >
                      <ChevronRight className="w-5 h-5 font-bold" />
                      Contact
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8 border-t border-white/20 text-sm">
            <div className="flex items-center gap-4 text-white/70 text-center md:text-left">
              <span>©{new Date().getFullYear()} Sierpe Frogs</span>
              <span className="w-px h-4 bg-white/20"></span>
              <span className="text-xs">A dream website by <a href="https://www.kreatiehoes.nl" target="_blank" rel="dofollow noopener noreferrer" className="underline hover:text-white transition-colors">Kreatiehoes</a></span>
              <span className="w-px h-4 bg-white/20"></span>
              <span className="text-xs">Photo Credit: <a href="https://www.facebook.com/raby.nunez/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white transition-colors">Raby Nuñez E.</a></span>
            </div>
            <div className="flex items-center gap-4 md:gap-8">
              <Link to="/terms-and-conditions" className="text-white/70 hover:text-white">Terms & Conditions</Link>
              <Link to="/privacy-policy" className="text-white/70 hover:text-white">Privacy Policy</Link>
              <a href="/sitemap.xml" className="text-white/70 hover:text-white">Sitemap</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;